import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_URL from '../config';
import './PomodoroTimer.css';

const WORK_SECONDS = 25 * 60;
const BREAK_SECONDS = 5 * 60;

export default function PomodoroTimer({ task, token, onClose, onPomodoroComplete }) {
  const [mode, setMode] = useState('work'); // 'work' | 'break'
  const [secondsLeft, setSecondsLeft] = useState(WORK_SECONDS);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionsCompleted, setSessions] = useState(task?.total_pomodoros || 0);
  const [minimized, setMinimized] = useState(false);
  const intervalRef = useRef(null);

  const totalSeconds = mode === 'work' ? WORK_SECONDS : BREAK_SECONDS;
  const progress = 1 - secondsLeft / totalSeconds;

  const recordPomodoro = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/tasks/${task.id}/pomodoro`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.total_pomodoros);
        if (onPomodoroComplete) onPomodoroComplete(task.id, data.total_pomodoros);
      }
    } catch (err) {
      console.error('Failed to record pomodoro:', err);
    }
  }, [task.id, token, onPomodoroComplete]);

  useEffect(() => {
    if (!isRunning) return;

    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setIsRunning(false);

          if (mode === 'work') {
            // Work session completed
            recordPomodoro();
            // Auto-switch to break
            setMode('break');
            return BREAK_SECONDS;
          } else {
            // Break completed, ready for next work session
            setMode('work');
            return WORK_SECONDS;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [isRunning, mode, recordPomodoro]);

  const toggleTimer = () => setIsRunning(prev => !prev);

  const resetTimer = () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);
    setMode('work');
    setSecondsLeft(WORK_SECONDS);
  };

  const skipPhase = () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);
    if (mode === 'work') {
      setMode('break');
      setSecondsLeft(BREAK_SECONDS);
    } else {
      setMode('work');
      setSecondsLeft(WORK_SECONDS);
    }
  };

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeDisplay = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  // SVG circle dimensions
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  if (minimized) {
    return (
      <div className={`pomodoro-minimized pomodoro-${mode}`} onClick={() => setMinimized(false)}>
        <span className="pomodoro-mini-icon">🍅</span>
        <span className="pomodoro-mini-time">{timeDisplay}</span>
        {isRunning && <span className="pomodoro-mini-pulse" />}
      </div>
    );
  }

  return (
    <div className={`pomodoro-overlay pomodoro-${mode}`}>
      <div className="pomodoro-panel">
        {/* Header */}
        <div className="pomodoro-header">
          <div className="pomodoro-mode-badge">
            {mode === 'work' ? '🍅 Focus Time' : '☕ Break Time'}
          </div>
          <div className="pomodoro-header-actions">
            <button className="pomodoro-btn-icon" onClick={() => setMinimized(true)} title="Minimize">─</button>
            <button className="pomodoro-btn-icon" onClick={onClose} title="Close">✕</button>
          </div>
        </div>

        {/* Task Name */}
        <div className="pomodoro-task-name" title={task.text}>
          {task.text}
        </div>

        {/* Circular Progress */}
        <div className="pomodoro-ring-container">
          <svg className="pomodoro-ring" width="200" height="200" viewBox="0 0 200 200">
            {/* Background circle */}
            <circle
              cx="100" cy="100" r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="8"
            />
            {/* Progress circle */}
            <circle
              cx="100" cy="100" r={radius}
              fill="none"
              stroke={mode === 'work' ? '#ef4444' : '#22c55e'}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 100 100)"
              className="pomodoro-progress-arc"
            />
          </svg>
          <div className="pomodoro-time-display">
            <span className="pomodoro-time">{timeDisplay}</span>
            <span className="pomodoro-phase-label">{mode === 'work' ? 'FOCUS' : 'BREAK'}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="pomodoro-controls">
          <button className="pomodoro-ctrl-btn" onClick={resetTimer} title="Reset">
            ↻
          </button>
          <button className={`pomodoro-play-btn ${isRunning ? 'running' : ''}`} onClick={toggleTimer}>
            {isRunning ? '⏸' : '▶'}
          </button>
          <button className="pomodoro-ctrl-btn" onClick={skipPhase} title="Skip">
            ⏭
          </button>
        </div>

        {/* Sessions Count */}
        <div className="pomodoro-sessions">
          <span className="pomodoro-session-count">🍅 {sessionsCompleted} session{sessionsCompleted !== 1 ? 's' : ''} completed</span>
        </div>
      </div>
    </div>
  );
}
